/*
  Shared info modals (snippets/sw-modal.liquid).

  Any element with data-sw-modal-open="{handle}" opens #SwModal-{handle}.
  Desktop is a centered dialog; mobile is a bottom sheet that also closes on a
  downward swipe. Focus returns to the trigger, and the scrollbar is replaced
  with padding so the page does not shift.
*/
(() => {
  if (customElements.get('sw-modal-root')) return;

  const OPEN = 'data-sw-modal-open';
  const LOCK = 'sw-modal-open';

  const lockScroll = () => {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--sw-scrollbar-gap', `${gap}px`);
    document.documentElement.classList.add(LOCK);
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove(LOCK);
    document.documentElement.style.removeProperty('--sw-scrollbar-gap');
  };

  class SwModalRoot extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);

      this.querySelectorAll('[data-sw-modal]').forEach((dialog) => {
        dialog.addEventListener('close', () => this.onClosed(dialog));
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close(dialog);
        });
        this.bindSwipe(dialog);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    dialogFor(id) {
      return id ? this.querySelector(`#SwModal-${CSS.escape(id)}`) : null;
    }

    onClick(event) {
      const trigger = event.target.closest(`[${OPEN}]`);
      if (trigger && this.contains(this.dialogFor(trigger.getAttribute(OPEN)))) {
        event.preventDefault();
        this.open(this.dialogFor(trigger.getAttribute(OPEN)), trigger);
        return;
      }

      const dialog = event.target.closest('[data-sw-modal]');
      if (!dialog || !dialog.open) return;
      if (event.target.closest('[data-sw-modal-close]')) {
        event.preventDefault();
        this.close(dialog);
        return;
      }
      /* Backdrop clicks target the dialog itself. Compare against the panel's box, so a tap on
         the panel's own padding does not count as outside. Keyboard clicks report 0,0. */
      if (event.target !== dialog || (event.clientX === 0 && event.clientY === 0)) return;
      const box = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= box.left &&
        event.clientX <= box.right &&
        event.clientY >= box.top &&
        event.clientY <= box.bottom;
      if (!inside) this.close(dialog);
    }

    onKeydown(event) {
      const dialog = this.querySelector('[data-sw-modal][open]');
      if (!dialog || event.key !== 'Tab') return;
      const focusable = this.focusable(dialog);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    focusable(dialog) {
      return Array.from(
        dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null || element === document.activeElement);
    }

    open(dialog, trigger) {
      if (!dialog) return;
      if (dialog.open && dialog.dataset.closing !== 'true') return;
      this.cancelClose(dialog);
      if (dialog.open) {
        dialog.dataset.closing = 'false';
        return;
      }
      const current = this.querySelector('[data-sw-modal][open]');
      if (current) {
        this.cancelClose(current);
        current.close();
      }
      dialog.swTrigger = trigger;
      lockScroll();
      dialog.showModal();
      const close = dialog.querySelector('[data-sw-modal-close]');
      window.requestAnimationFrame(() => close?.focus({ preventScroll: true }));
    }

    cancelClose(dialog) {
      if (dialog.swCloseTimer) window.clearTimeout(dialog.swCloseTimer);
      if (dialog.swCloseEnd) dialog.removeEventListener('transitionend', dialog.swCloseEnd);
      dialog.swCloseTimer = null;
      dialog.swCloseEnd = null;
    }

    close(dialog) {
      if (!dialog?.open || dialog.dataset.closing === 'true') return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        dialog.close();
        return;
      }
      dialog.dataset.closing = 'true';
      const finish = () => {
        this.cancelClose(dialog);
        if (dialog.dataset.closing !== 'true') return;
        dialog.dataset.closing = 'false';
        if (dialog.open) dialog.close();
      };
      /* Only the panel's own transform ends the close; children and the backdrop also emit transitionend. */
      dialog.swCloseEnd = (event) => {
        if (event.target === dialog && !event.pseudoElement && event.propertyName === 'transform') finish();
      };
      dialog.addEventListener('transitionend', dialog.swCloseEnd);
      dialog.swCloseTimer = window.setTimeout(finish, 360);
    }

    onClosed(dialog) {
      this.cancelClose(dialog);
      dialog.dataset.closing = 'false';
      if (!this.querySelector('[data-sw-modal][open]')) unlockScroll();
      const trigger = dialog.swTrigger;
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    }

    bindSwipe(dialog) {
      const sheet = dialog.querySelector('[data-sw-modal-sheet]');
      const grab = dialog.querySelector('[data-sw-modal-grab]');
      if (!sheet || !grab) return;
      let startY = null;

      grab.addEventListener('pointerdown', (event) => {
        if (window.matchMedia('(min-width: 750px)').matches) return;
        startY = event.clientY;
        sheet.style.transition = 'none';
        grab.setPointerCapture(event.pointerId);
      });

      grab.addEventListener('pointermove', (event) => {
        if (startY === null) return;
        const dy = Math.max(0, event.clientY - startY);
        sheet.style.transform = `translateY(${dy}px)`;
      });

      const end = (event) => {
        if (startY === null) return;
        const dy = event.clientY - startY;
        startY = null;
        sheet.style.transition = '';
        sheet.style.transform = '';
        if (dy > 60) this.close(dialog);
      };
      grab.addEventListener('pointerup', end);
      grab.addEventListener('pointercancel', end);
    }
  }

  customElements.define('sw-modal-root', SwModalRoot);
})();
